/**
 * Right-side floating chat panel (PLT-4625).
 *
 * The panel reuses the same `UprightChatInner` rendered by the `/chat`
 * route, hosted under the same app-root `ChatRuntimeProvider`. That means:
 *
 *   - All chat features (MCP App cards, citations, web-search toggle,
 *     internal-mode debug panel, attachment composer) work in the panel
 *     without any additional wiring.
 *   - When the user clicks "Expand to full chat", we just navigate to
 *     `/chat` (with no `?prompt=`) and unmount the panel — the lifted
 *     runtime carries the conversation forward seamlessly.
 *
 * The panel is intentionally non-modal: the page underneath stays
 * interactive (no scroll lock, no focus trap). This matches the request to
 * keep the floating "modalish" feel without trapping the user.
 *
 * Self-gated: the component subscribes to `chat-panel-state` via
 * `useSyncExternalStore` and renders nothing when the panel is closed,
 * so it is safe to mount unconditionally at the app root.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '@primer/react';
import { useAssistantRuntime } from '@assistant-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import TaillessLineArrowLeft1Icon from '@streamlinehq/streamlinehq/img/streamline/tailless-line-arrow-left-1.svg';
import { DeleteSteamlineCore, SearchStreamlineCore } from '@upright/ui/icons';

import { useGetAllSettings } from '@upright/ui/api/internal/settings';
import {
  closeChatPanel,
  consumeChatPanelSeed,
  getChatPanelState,
  type OpenChatPanelOptions,
  subscribeChatPanelState,
} from './chat-panel-state';
import { ChatSidePanelChats } from './chat-side-panel-chats';
import { rehydrateConversation } from './history/chat-history-actions';
import { expireChatConversationLists } from './history/chat-history-data';
import { setActiveConversationId } from './history/chat-history-state';
import { NewChatButton } from './upright-chat-composer';
import { UprightChatInner } from './upright-chat';
import { ChatSurfaceContext } from './chat-surface';
import {
  composerState,
  getPageChatContext,
  type HiddenEntityContext,
  hiddenEntityContext,
  type PageChatContext,
  requestComposerFocus,
  resetChatLoggingState,
  setPageChatContext,
  setSeedingInitialPrompt,
  setWebSearchChoice,
} from './upright-chat-shared';

/**
 * External-link arrow used as the "expand to full chat" affordance — same
 * icon shape rendered by `ExternalLinkIcon` in `src/ui/mcp-apps/common/
 * components.tsx`, kept inline here so the visual matches MCP App card
 * headers exactly without forcing the chat surface to import iframe-bundle
 * code. If the MCP-app icon is ever updated, mirror the change here.
 */
function ExpandToFullChatIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

export function ChatSidePanel() {
  const state = useSyncExternalStore(subscribeChatPanelState, getChatPanelState);
  // PLT-4679: global kill switch. Render nothing when chat is disabled so the
  // side panel stops appearing for every entry point (green Net Impact bars,
  // DMA "Ask AI" icon, header "Ask AI" link, …) without having to teach each
  // entry point about the setting. The backend rejects `POST /api/chat`
  // anyway, but unmounting the panel keeps the UI clean during the outage.
  const { data: settings } = useGetAllSettings();

  if (!state.open) return null;
  if (settings && settings.enableAiChat === false) return null;

  // `seedNonce` increments on every `openChatPanel(...)` call, each of
  // which starts a new conversation (PLT-7880).
  // Using it as a React `key` forces a clean unmount/mount cycle of
  // `ChatSidePanelOpen` whenever such a seed arrives — including
  // the `bar click → expand to /chat → browser-back → bar click` flow,
  // where the panel was previously closed but the runtime had a thread
  // from a prior session. A fresh mount means the seed-consumption
  // effect always fires against a fresh component tree, so the panel
  // never "forgets" to auto-send the new prompt. (PLT-4625 follow-up
  // "going to full chat, back and clicking a green bar opens the chat
  // in the initial state".)
  return <ChatSidePanelOpen key={state.seedNonce} pendingSeed={state.pendingSeed} />;
}

/**
 * Body — only mounted when the panel is open. Splitting into an inner
 * component lets us call `useAssistantRuntime` (which only works inside
 * `<AssistantRuntimeProvider>`) without paying for the hook on every
 * render of every page in the app.
 *
 * `pendingSeed` is the seed currently published by the chat-panel-state
 * store, threaded down from `ChatSidePanel`. We use its identity as the
 * dependency for the seeding effect so a fresh `openChatPanel` call
 * (which always allocates a new options object) re-fires the effect even
 * when the panel was already open. The store's `consumeChatPanelSeed`
 * intentionally does *not* notify when it clears the internal slot, so
 * the captured `pendingSeed` value here remains stable until the next
 * notify — at which point its identity changes and the effect runs.
 */
function ChatSidePanelOpen({ pendingSeed }: { pendingSeed: ReturnType<typeof getChatPanelState>['pendingSeed'] }) {
  const navigate = useNavigate();
  const runtime = useAssistantRuntime();
  const queryClient = useQueryClient();
  // Which of the two the body shows: the conversation, or the Chats view of every conversation
  // (PLT-7883). The view and its search last as long as the panel: this component remounts on
  // every open, so a reopened panel is back on the conversation with an empty search.
  const [view, setView] = useState<'chat' | 'chats'>('chat');
  const [chatsQuery, setChatsQuery] = useState('');
  // The conversation last picked in the Chats view, which `rehydrateConversation` checks at each
  // step, so a pick still loading when the next is made gives way to it. Here rather than in the
  // view, which unmounts on the pick.
  const rehydrationTokenRef = useRef<string | null>(null);
  // The page's binding (chip and entity) the panel had before a past chat replaced its
  // conversation. Opening a past chat clears it with the rest of the chat state, and the page under
  // the panel publishes its binding only once, on mount, so without this a `New chat` afterwards
  // would be about no page at all, and a consultant's turns would carry no company. `New chat`
  // puts it back.
  const pageBindingRef = useRef<{ pageContext: PageChatContext | null; entity: HiddenEntityContext | null } | null>(
    null,
  );

  // Consume each new pending seed exactly once.
  //
  // Every seed starts a new conversation (PLT-7783 for suggested
  // questions, PLT-7880 for every other way in): `switchToNewThread` +
  // `resetChatLoggingState`, then publish the seed's binding and chip and
  // append its prompt, if any, as the first message of the new thread.
  // Mirrors `InitialPromptAutoSender` on the `/chat?prompt=…` deep-link,
  // which is the same shared-runtime + auto-send pattern and is known to
  // work reliably.
  //
  // **Order matters here**, two ways:
  //
  // 1. `resetChatLoggingState` MUST run *before* we publish the new
  //    `hiddenEntityContext` / `pageChatContext` / `composerState.
  //    pendingSource` for this seed. The reset clears all three
  //    (entity binding, chip label, source tag); if the reset ran
  //    *after* the publish, it would wipe the very state the seed is
  //    trying to seed, leaving the panel auto-submitting a prompt with
  //    no entity context and no chip.
  //
  // 2. The thread switch is awaited before anything is published or
  //    appended, the same way `InitialPromptAutoSender` does it.
  //    `switchToNewThread` defers the main-thread id update behind a
  //    microtask, so appending synchronously after it lands the prompt
  //    on the thread being switched away from: on an empty thread the
  //    two happen to coincide, but from a thread that already holds a
  //    conversation the question vanishes and the panel shows the
  //    welcome screen (PLT-5240, PLT-7783). There is deliberately no
  //    cancellation flag: a re-render that re-snapshots
  //    `pendingSeed === null` would trip it and silently abort the
  //    append (PLT-4625 follow-up "Same issue with the chat opening in
  //    wrong state again"). While the switch is pending,
  //    `setSeedingInitialPrompt` holds the welcome pills inert.
  //
  // The dep is `pendingSeed` (object identity) rather than `[]` because
  // a second `openChatPanel` call while the panel is already open swaps
  // in a fresh seed object — re-running the effect is what makes the
  // new question land in the panel rather than getting silently dropped.
  useEffect(() => {
    if (pendingSeed === null) return;
    const seed = consumeChatPanelSeed();
    if (seed === null) return;

    setSeedingInitialPrompt(true);
    void (async () => {
      // Cleared in `finally` so a throw cannot leave the welcome pills inert.
      try {
        try {
          await runtime.threads.switchToNewThread();
        } catch (err) {
          // Fall through and seed the current thread rather than dropping the question.
          console.error('Chat panel: switching to a new chat thread failed; seeding the current thread.', err);
        }
        // Reset before publishing — this clears `hiddenEntityContext`,
        // `pageChatContext`, `composerState.pendingSource` and the
        // chat-debug store.
        resetChatLoggingState();
        publishSeed(seed);
      } finally {
        setSeedingInitialPrompt(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSeed]);

  function publishSeed(seed: OpenChatPanelOptions) {
    // A launcher's EU web search choice (PLT-7271). After the reset, which
    // would otherwise turn it back off.
    if (seed.webSearch !== undefined) {
      setWebSearchChoice(seed.webSearch.provider, seed.webSearch.enabled);
    }

    // Now publish the seed-derived state. Order matters: the chat
    // adapter reads `hiddenEntityContext` and `pageChatContext`
    // directly on every send, so they need to be set before the
    // `runtime.thread.append` call below.
    //
    // `seed.extra` (PLT-4676) carries row-level identifiers from panel
    // openers like the R/O table's "Modelling details" icon (matterId,
    // valence, driver, …, intent). They flow through `hiddenEntity
    // Context.value.extra` and get appended to the same `[Context: …]`
    // line as the entity binding by the chat adapter, so the model can
    // pin its answer to one specific row rather than summarising the
    // whole matter.
    if (seed.entityId !== undefined && seed.entityType !== undefined) {
      hiddenEntityContext.value = {
        entityId: seed.entityId,
        entityType: seed.entityType,
        ...(seed.extra ? { extra: seed.extra } : {}),
      };
    }
    // Visible page-context chip + hidden URL context. Goes through the
    // setter so subscribed components (the chip itself) re-render.
    //
    // Fold the seed's `entityType` into the page context so the welcome
    // screen can frame its suggestion chips for the right entity even when
    // no entity view is mounted to publish a sticky binding (PLT-4938).
    // Producers that already set `entityType` on their `pageContext` win;
    // otherwise we backfill it from the seed's top-level `entityType`.
    setPageChatContext(
      seed.pageContext != null
        ? { ...seed.pageContext, entityType: seed.pageContext.entityType ?? seed.entityType }
        : null,
    );
    if (seed.source !== undefined && seed.source !== 'dimension_bar') {
      // `composerState.pendingSource` only models the analytics-tag union,
      // not our wider `ChatPanelMessageSource`. Future-proofing: when
      // `dimension_bar` graduates from a panel-only label into the
      // composer-state union, drop this branch.
      composerState.pendingSource = seed.source;
    }

    // Bind-only seeds (PLT-4665 — `Ask AI` top-bar link on entity pages)
    // carry no `prompt`, so we skip the auto-append. The entity binding
    // and page-context chip have already been published above, so when
    // the user composes and sends their own first message it will be
    // tagged with the right context.
    if (seed.prompt !== undefined) {
      runtime.thread.append(seed.prompt);
    }
  }

  // Esc closes the panel — non-trapping, mirrors most overlay UX. We use a
  // capture-phase listener at the window level rather than focusing the
  // panel root because the user may be focused inside the composer
  // textarea and we still want Esc to close.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        closeChatPanel();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  function handleExpand() {
    closeChatPanel();
    navigate('/chat');
  }

  // The conversation the panel is showing, for the Chats view to mark. The thread's id once it
  // holds a message: a new chat with nothing sent is in no list yet. Not `useActiveConversationId`,
  // which only the `/chat` sidebar and the investor rail keep in step with a running turn, and on
  // most pages under the panel neither is mounted.
  const currentConversationId =
    view === 'chats' && runtime.thread.getState().messages.length > 0 ? composerState.threadId : null;

  function showChats() {
    expireChatConversationLists(queryClient);
    setView('chats');
  }

  function showChat() {
    setView('chat');
    requestComposerFocus();
  }

  // Runs before `NewChatButton` takes the binding it keeps across the reset, so restoring the
  // page's binding here is what the new chat carries. Clearing the token makes a past chat still
  // loading give way to the new one, rather than replacing it once it has loaded.
  function startNewChatOnPage() {
    rehydrationTokenRef.current = null;
    const saved = pageBindingRef.current;
    if (saved !== null) {
      pageBindingRef.current = null;
      setPageChatContext(saved.pageContext);
      hiddenEntityContext.value = saved.entity;
    }
    showChat();
  }

  function openConversation(id: string) {
    showChat();
    if (id === currentConversationId) return;
    pageBindingRef.current ??= { pageContext: getPageChatContext(), entity: hiddenEntityContext.value };
    // The same open the `/chat` sidebar makes on a row click (`ChatHistoryList`): the row's
    // conversation marked current straight away, then loaded into the shared runtime.
    setActiveConversationId(id);
    rehydrationTokenRef.current = id;
    void rehydrateConversation({ id, runtime, queryClient, rehydrationTokenRef });
  }

  return (
    <div
      className={view === 'chats' ? 'chat-side-panel chat-side-panel--chats' : 'chat-side-panel'}
      role="complementary"
      aria-label="Ask AI"
    >
      <div className="chat-side-panel-header">
        {view === 'chats' ? (
          <>
            <Tooltip text="Back to chat" direction="s">
              <button
                type="button"
                className="chat-side-panel-icon-button"
                onClick={showChat}
                aria-label="Back to chat"
              >
                <TaillessLineArrowLeft1Icon width={16} height={16} aria-hidden="true" />
              </button>
            </Tooltip>
            <div className="chat-side-panel-title">Chats</div>
          </>
        ) : (
          <div className="chat-side-panel-title">Ask AI</div>
        )}
        {/*
         * Header action cluster — order matters (PLT-4928):
         *   1. "Expand to full view" — promoted to a labelled stroke pill
         *      and placed leftmost (closest to the title) because it's
         *      the action we most want to advertise from the panel
         *      header. The icon-only version was difficult to find.
         *      The label removes the discoverability problem; the
         *      stroke border makes it read as a real button without
         *      adding a heavy fill that would compete with the title.
         *   2. "Search chats" — opens the Chats view (PLT-7883), left of
         *      New chat as on the investor rail's Chats group.
         *   3. "New chat" — secondary, kept as a 28×28 icon button.
         *      Starts over mid-conversation without leaving the panel.
         *   4. "Close" — last, by convention (close affordances live
         *      at the far end of a header row).
         * The Chats view drops the first two: there is no conversation on
         * screen to expand, and the view is already the search. New chat
         * and Close keep their places at the right end.
         */}
        {view === 'chat' && (
          <>
            <button
              type="button"
              className="chat-side-panel-expand-pill"
              onClick={handleExpand}
              aria-label="Expand to full view"
            >
              <ExpandToFullChatIcon />
              <span className="chat-side-panel-expand-pill-label">Expand to full view</span>
            </button>
            <Tooltip text="Search chats" direction="s">
              <button
                type="button"
                className="chat-side-panel-icon-button"
                onClick={showChats}
                aria-label="Search chats"
              >
                <SearchStreamlineCore width={16} height={16} aria-hidden="true" />
              </button>
            </Tooltip>
          </>
        )}
        <NewChatButton variant="icon" preservePageContext onNewChat={startNewChatOnPage} />
        <Tooltip text="Close" direction="s">
          <button
            type="button"
            className="chat-side-panel-icon-button"
            onClick={closeChatPanel}
            aria-label="Close chat panel"
          >
            <DeleteSteamlineCore width={16} height={16} />
          </button>
        </Tooltip>
      </div>
      {/*
       * The conversation stays mounted under the Chats view, only hidden, so going back finds it as
       * it was left: its scroll position, a turn still streaming, and its cards without reloading.
       */}
      <div className="chat-side-panel-body" hidden={view === 'chats'}>
        <ChatSurfaceContext.Provider value="side-panel">
          <UprightChatInner />
        </ChatSurfaceContext.Provider>
      </div>
      {view === 'chats' && (
        <ChatSidePanelChats
          query={chatsQuery}
          onSearch={setChatsQuery}
          currentConversationId={currentConversationId}
          onOpenConversation={openConversation}
        />
      )}
    </div>
  );
}
